import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  PeriodTypeEntityT,
  PeriodEntityT,
} from "../domain/entities/periodType.entity";
import type { PeriodTypeUiT, PeriodUiT } from "../domain/ui/periodType.model";
import type { PeriodTypesRepositoryT } from "../domain/ports/periodTypes.repository";

import { getPeriodTypes } from "../application/useCases/getPeriodTypes";
import { importPeriodTypes } from "../application/useCases/importPeriodTypes";

const formatDate = (isoDate: string | null) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

const toPeriodUiModel = (period: PeriodEntityT): PeriodUiT => ({
  id: period.periodId,
  name: period.name,
  label: `${period.name} - ${formatDate(period.initialDate)} al ${formatDate(period.finalDate)}`,
  initialDate: period.initialDate,
  finalDate: period.finalDate,
});

export const toUiModel = (data: PeriodTypeEntityT[]): PeriodTypeUiT[] =>
  data
    .map((item) => ({
      id: item.periodType,
      name: item.name,
      periods: item.periods.map(toPeriodUiModel),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

export const createUsePeriodTypes = ({
  periodTypesRepository,
}: {
  periodTypesRepository: PeriodTypesRepositoryT;
}) => {
  return function usePeriodTypes() {
    const queryClient = useQueryClient();

    const query = useQuery({
      queryKey: ["shared/periodTypes"],
      queryFn: () => getPeriodTypes({ periodTypesRepository }),
      refetchOnWindowFocus: false,
      gcTime: 0, // Deletes data from cache immediately after unmount
      select: toUiModel,
    });

    const importPeriodTypesMutation = useMutation({
      mutationFn: ({ periodTypeId }: { periodTypeId: string }) =>
        importPeriodTypes({ periodTypesRepository, periodTypeId }),
      onSuccess: (result) => {
        queryClient.setQueryData(["shared/periodTypes"], result);
      },
    });

    return {
      ...query,
      importPeriodTypes: importPeriodTypesMutation,
    };
  };
};
